import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from '@/components/layout';
import { BandPill, Button, Calendar, Card, Clipboard, Home, Illustration, Lock, Refresh, Skeleton, StatusPill } from '@/components/ui';
import { cx } from '@/components/ui/cx';
import { useT } from '@/i18n';
import { useSession } from '@/hooks/session';
import { formatDate, formatMoney, priceLabel } from '@/lib/format';
import { sceneImage } from '@/lib/illustrations';
import { api } from '@/api/client';
import type { ReportSummary } from '@/api/types';
import { AskBehaviourCard } from '@/features/behaviour/AskBehaviourCard';
import { DocumentView } from '@/features/report/DocumentView';
import { ReportActions } from '@/features/report/ReportActions';
import { useReportPolling } from '@/features/report/useReportPolling';
import { GuidanceView } from '@/features/report/GuidanceView';

const tabClass = (active: boolean) =>
  cx(
    'inline-flex min-h-tap items-center gap-2 rounded-md px-3 text-label transition-colors',
    active ? 'bg-white text-ink shadow-1' : 'text-white/85 hover:bg-white/10',
  );

const segmentClass = (active: boolean) =>
  cx(
    'inline-flex min-h-tap items-center rounded-pill px-4 text-label font-extrabold transition-colors',
    active ? 'bg-lagoon-600 text-white' : 'text-ink-muted hover:text-lagoon-600',
  );

const INCLUDED = [
  [Home, 'r2.inc1', 'r2.inc1b'],
  [Calendar, 'r2.inc2', 'r2.inc2b'],
  [Clipboard, 'r2.inc3', 'r2.inc3b'],
  [Refresh, 'r2.inc4', 'r2.inc4b'],
] as const;
/** v1.3 pastel fills for the "what's included" icons, in list order. */
const INCLUDED_TINT = ['bg-lavender-200', 'bg-peach-200', 'bg-mint-200', 'bg-lavender-200'] as const;

export const Report = () => {
  const t = useT();
  const navigate = useNavigate();
  const { reportId = '' } = useParams<{ reportId: string }>();
  const { config } = useSession();
  const { data, loading, error, refresh } = useReportPolling(reportId);
  const [siblings, setSiblings] = useState<ReportSummary[]>([]);
  const [retrying, setRetrying] = useState(false);
  // The session home programme is the default read once it exists; the area report stays a tap away.
  const [view, setView] = useState<'programme' | 'area'>('programme');

  const assessmentId = data?.report.assessmentId;

  // Sibling reports from the same assessment session (e.g. Milestones + Everyday skills +
  // Connecting, generated together) — rendered as a tab strip above the document.
  useEffect(() => {
    if (!assessmentId) return undefined;
    let cancelled = false;
    void api.reports
      .list()
      .then(({ reports }) => {
        if (!cancelled) setSiblings(reports.filter((r) => r.assessmentId === assessmentId));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  const pricing = config?.pricing.IN;
  const price = pricing ? priceLabel(pricing) : undefined;

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await api.reports.retry(reportId);
      await refresh();
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <AppShell>
        <Skeleton lines={6} />
      </AppShell>
    );
  }

  if (error || !data) {
    return (
      <AppShell>
        <Card tone="sage">
          <p className="text-label text-ink">{t('common.error')}</p>
        </Card>
      </AppShell>
    );
  }

  const { report, content } = data;

  if (report.kind === 'guidance') {
    const plan = config?.plans.unlock;
    return (
      <AppShell>
        <GuidanceView
          data={data}
          unlockPrice={plan ? { amount: plan.launchAmount, currency: plan.currency } : undefined}
          retrying={retrying}
          onRetry={() => void handleRetry()}
        />
      </AppShell>
    );
  }
  const assessment = content.assessment;
  const programme = content.programme;
  const showProgramme = !!programme && view === 'programme';
  const document = showProgramme ? programme : content.document;
  const preparedAt = document?.preparedAt ?? report.createdAt;

  const unlocked = content.tier === 'full';
  const unlockPlan = config?.plans.unlock;

  return (
    <AppShell>
      <header className="relative overflow-hidden rounded-xl bg-lagoon-600 px-5 pb-0 pt-6 text-white md:px-8 md:pt-8">
        <span aria-hidden="true" className="pointer-events-none absolute -right-20 -top-28 h-[380px] w-[380px] rounded-full border-[56px] border-sage-500/25" />
        {siblings.length > 1 && (
          <div role="tablist" aria-label={t('reports.title')} className="relative mb-5 flex flex-wrap gap-2">
            {siblings.map((sib) => (
              <Link
                key={sib.reportId}
                role="tab"
                aria-selected={sib.reportId === report.reportId}
                to={`/reports/${sib.reportId}`}
                className={tabClass(sib.reportId === report.reportId)}
              >
                <span>{sib.area ?? sib.assessmentKey}</span>
                {sib.band && sib.bandLabel && <BandPill band={sib.band} label={sib.bandLabel} />}
                {sib.source === 'formal' && (
                  <span className="inline-flex items-center rounded-pill bg-white/15 px-2.5 py-0.5 text-caption">
                    {t('report.fromFormal')}
                  </span>
                )}
              </Link>
            ))}
          </div>
        )}
        <div className="relative grid items-end gap-6 md:grid-cols-[150px_1fr_auto]">
          <div aria-hidden="true" className="hidden h-[178px] w-[150px] overflow-hidden rounded-arch border-[5px] border-b-0 border-coral-500 bg-white md:block">
            <Illustration src={sceneImage('report-ready-lap')} alt="" position="50% 35%" eager />
          </div>
          <div className="pb-6">
            <p className="eyebrow text-coral-300">{t('r2.ready', { date: formatDate(preparedAt) })}</p>
            <h1 className="mt-1 text-h1 text-white">{report.childName}</h1>
            {showProgramme ? (
              <p className="text-body text-[#CFE4E1]">{t('programme.title', { week: programme.week })}</p>
            ) : (
              assessment && <p className="text-body text-[#CFE4E1]">{assessment.label}</p>
            )}
            <p className="mt-2 text-caption text-[#A9CFD0]">
              {report.ref} · {t('r2.note')}
            </p>
          </div>
          <div className="flex items-center gap-2 pb-6">
            {assessment && <BandPill band={assessment.band} label={assessment.bandLabel} />}
            {assessment?.source === 'formal' && (
              <span className="inline-flex items-center rounded-pill bg-white/15 px-2.5 py-0.5 text-caption">
                {t('report.fromFormal')}
              </span>
            )}
            <StatusPill status={report.status} />
          </div>
        </div>
      </header>

      {report.status === 'unlocked' && (
        <div className="mt-6 empty:hidden">
          <AskBehaviourCard assessmentId={report.assessmentId} />
        </div>
      )}

      <div className="mt-6 grid gap-6 nav:grid-cols-[1fr_360px] nav:items-start">
        <div className="min-w-0">
          {programme && content.document && (
            <div role="group" aria-label={t('programme.viewSwitch')} className="inline-flex rounded-pill bg-white p-1 shadow-1">
              <button type="button" aria-pressed={view === 'programme'} onClick={() => setView('programme')} className={segmentClass(view === 'programme')}>
                {t('programme.view')}
              </button>
              <button type="button" aria-pressed={view === 'area'} onClick={() => setView('area')} className={segmentClass(view === 'area')}>
                {t('programme.areaView')}
              </button>
            </div>
          )}

          {showProgramme && programme.watermark && (
            <Card tone="apricot" className="mt-4">
              <p className="text-body">{t('programme.provisional')}</p>
            </Card>
          )}

          <div className="mt-5">
            {document ? (
              <DocumentView
                document={document}
                onUnlock={() => navigate(`/sessions/${report.assessmentId}/checkout`)}
                price={price}
              />
            ) : report.status === 'failed' ? (
              <Card tone="sage">
                <p className="text-label text-ink">{t('gen.failed')}</p>
                <p className="mt-1 text-caption text-ink-muted">{t('gen.failedBody')}</p>
                <Button variant="secondary" className="mt-3" loading={retrying} onClick={() => void handleRetry()}>
                  {t('gen.retry')}
                </Button>
              </Card>
            ) : (
              <>
                <Skeleton lines={6} />
                <p className="mt-4 text-caption text-ink-muted">{t('report.generating')}</p>
              </>
            )}
          </div>
        </div>

        <aside className="flex flex-col gap-5 nav:sticky nav:top-24">
          {document && !unlocked && (
            <section aria-labelledby="unlock-title" className="overflow-hidden rounded-xl bg-white shadow-2">
              <div className="flex items-center gap-3 bg-coral-100 px-5 py-4">
                <Lock className="h-6 w-6 shrink-0 text-coral-ink" />
                <div>
                  <h2 id="unlock-title" className="text-[21px] leading-tight">{t('r2.unlockTitle', { child: report.childName })}</h2>
                  <p className="text-caption text-coral-ink">{t('r2.unlockSub')}</p>
                </div>
              </div>
              <ul className="flex flex-col gap-3 px-5 py-4">
                {INCLUDED.map(([Icon, title, sub], i) => (
                  <li key={title} className="grid grid-cols-[36px_1fr] items-center gap-3">
                    <span aria-hidden="true" className={cx('flex h-9 w-9 items-center justify-center rounded-[11px] text-ink', INCLUDED_TINT[i])}>
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="text-label">
                      {t(title)}
                      <span className="block text-caption font-semibold text-ink-muted">{t(sub)}</span>
                    </span>
                  </li>
                ))}
              </ul>
              <div className="px-5 pb-5">
                {unlockPlan && (
                  <p className="mb-3 flex items-baseline gap-2">
                    <span className="font-display text-[38px] font-extrabold leading-none">{formatMoney(unlockPlan.launchAmount, unlockPlan.currency)}</span>
                    {unlockPlan.listAmount > unlockPlan.launchAmount && (
                      <s className="text-label text-ink-muted">{formatMoney(unlockPlan.listAmount, unlockPlan.currency)}</s>
                    )}
                    <span className="ml-auto rounded-pill bg-progress-soft px-2.5 py-0.5 text-caption text-progress-ink">{t('r2.launch')}</span>
                  </p>
                )}
                {unlockPlan && !unlockPlan.taxInclusive && (unlockPlan.taxAmount ?? 0) > 0 && (
                  <p className="-mt-2 mb-3 text-caption text-ink-muted">{t('plan.taxExtra', { label: unlockPlan.taxLabel })}</p>
                )}
                <Button variant="celebrate" full onClick={() => navigate(`/sessions/${report.assessmentId}/checkout`)}>
                  {t('checkout.title')}
                </Button>
                <p className="mt-3 flex items-center justify-center gap-1.5 text-caption text-ink-muted">
                  <Lock className="h-3.5 w-3.5" />
                  {t('r2.secure')}
                </p>
              </div>
            </section>
          )}

          {unlocked && content.programme && (
            <Link
              to={`/programme/${report.assessmentId}`}
              className="lift flex items-center gap-4 rounded-xl border-l-[6px] border-lavender-200 bg-white p-4 shadow-1"
            >
              <span className="h-20 w-24 shrink-0 overflow-hidden rounded-md">
                <Illustration src={sceneImage('programme-teaser')} alt="" />
              </span>
              <span>
                <span className="block font-display text-[19px] font-bold leading-tight text-ink">{t('r2.openProgramme')}</span>
                <span className="text-caption text-ink-muted">{t('r2.inc1b')}</span>
              </span>
            </Link>
          )}

          {document && (
            <ReportActions
              report={report}
              tier={content.tier}
              entitlement={data.entitlement}
              className="sticky bottom-0 z-10 -mx-4 border-t border-line bg-paper/95 px-4 py-4 backdrop-blur nav:static nav:mx-0 nav:rounded-xl nav:border-0 nav:bg-white nav:p-5 nav:shadow-1"
            />
          )}
        </aside>
      </div>
    </AppShell>
  );
};
